import Reveal from './Reveal';
import { Sparkles, ShieldCheck, Cpu } from 'lucide-react';

const features = [
    {
        icon: ShieldCheck,
        title: 'Automated Security Testing',
        desc: 'Detect vulnerabilities and security flaws automatically during the testing phase.',
        color: 'from-emerald-500 to-green-500',
        iconBg: 'bg-emerald-500/10',
        iconColor: 'text-emerald-400',
        borderColor: 'border-emerald-500/20',
        file: 'security-scan.ts',
    },
    {
        icon: Cpu,
        title: 'AI Code Generation',
        desc: 'Generate boilerplate and business logic based on your API definitions.',
        color: 'from-orange-500 to-amber-500',
        iconBg: 'bg-orange-500/10',
        iconColor: 'text-orange-400',
        borderColor: 'border-orange-500/20',
        file: 'ai-codegen.ts',
    }
];

const FutureWorkflow = () => {
    return (
        <section className="py-20 sm:py-28 relative overflow-hidden" id="vision">
            {/* Background glow */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-500/5 blur-[150px] rounded-full" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[300px] bg-cyan-500/5 blur-[120px] rounded-full" />
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="text-center max-w-4xl mx-auto mb-16 sm:mb-20">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-mono mb-6 sm:mb-8">
                        <Sparkles className="w-3.5 h-3.5" />
                        Coming Soon
                    </div>

                    <Reveal>
                        <h2 className="text-3xl sm:text-4xl md:text-6xl font-extrabold text-white tracking-tight mb-6 sm:mb-8">
                            The Future of{' '}
                            <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">Development Workflows</span>
                        </h2>

                        <p className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-3xl mx-auto mb-4">
                            We're pushing AI even further—so agents don't just assist, they proactively secure your APIs and generate production-ready code alongside your team.
                        </p>
                    </Reveal>
                </div>

                <div className="max-w-4xl mx-auto text-center">
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4 sm:mb-6 font-mono">
                        <span className="text-cyan-500/50">{'>'}</span> AI-Powered Development Ecosystem
                    </h3>
                    <p className="text-gray-500 text-base sm:text-lg leading-relaxed mb-10 sm:mb-14 max-w-2xl mx-auto">
                        Automated documentation, AI generation, mock servers, and role-based workspaces are already live today. Next up: automated security testing and full AI code generation.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 text-left">
                        {features.map((feature, i) => (
                            <Reveal key={i} delay={i * 100} className="h-full">
                                <div className={`h-full bg-[#111111] rounded-xl border border-white/5 hover:${feature.borderColor} transition-all duration-500 overflow-hidden group`}>
                                    {/* File tab */}
                                    <div className="flex items-center gap-2 px-4 py-2 bg-[#0d0d0d] border-b border-white/5">
                                        <div className="flex gap-1">
                                            <div className="w-2 h-2 rounded-full bg-[#ff5f57]/40" />
                                            <div className="w-2 h-2 rounded-full bg-[#febc2e]/40" />
                                            <div className="w-2 h-2 rounded-full bg-[#28c840]/40" />
                                        </div>
                                        <span className="font-mono text-label text-gray-700 ml-1">{feature.file}</span>
                                    </div>
                                    <div className="flex items-start gap-4 sm:gap-5 p-5 sm:p-7">
                                        <div className={`w-11 h-11 sm:w-12 sm:h-12 rounded-lg ${feature.iconBg} border ${feature.borderColor} flex items-center justify-center ${feature.iconColor} flex-shrink-0 group-hover:scale-110 transition-transform`}>
                                            <feature.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                                        </div>
                                        <div>
                                            <h4 className="text-base sm:text-lg font-bold text-white mb-1.5 font-mono">{feature.title}</h4>
                                            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{feature.desc}</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FutureWorkflow;
